<template>
  <div class="z-[1] relative bg-white" data-collection-section>
    <div class="z-[1] relative border-t border-black">
      <div class="animated-gradient grid gap-8 lg:grid-cols-2 lg:gap-16 mx-auto px-4 py-8 sm:px-6 lg:px-8 pb-16 collection-nav-spacer mb-12 collection-hero" style="padding-top: calc(var(--demo-alert-height, 0px) + 8rem);">
        <div class="flex flex-col justify-center gap-10 mx-auto text-center max-w-5xl lg:col-span-2 ">
          <div class="flex flex-col max-w-screen-sm gap-8 lg:gap-10 lg:max-w-screen-md">
          <div class="text-xl md:text-2xl">{{ collectionPeriod }}</div>
          <h1 class="text-5xl font-medium md:text-6xl md:leading-[0.9] lg:text-8xl">{{ collectionHeading }}</h1>
          <div class="text-xl md:text-2xl">{{ collectionDescription }}</div>
          <div class="flex items-center justify-center gap-3">
              <span class="relative flex size-3">
                <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                <span class="relative inline-flex size-3 rounded-full bg-red-500"></span>
              </span>
              <strong class="">{{ formattedCountdown || '—' }}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Collection Explanation Section -->
      <div class="grid gap-8 lg:grid-cols-2 lg:gap-16 mx-auto max-w-screen-2xl px-4 py-8 sm:px-6 lg:px-8">
        <div class="flex flex-col gap-6 lg:col-span-2 max-w-3xl mx-auto">
          <!-- Intro Text -->
          <div class="text-center text-lg md:text-xl text-black/80 leading-relaxed">
            {{ t('pages.collection.explanation.intro') }}
          </div>

          <!-- Expandable "What am I looking at?" Section -->
          <div class="my-4 mb-12">
            <button
              @click="showDetails = !showDetails"
              class="w-full flex items-center justify-between gap-4 p-4 text-left border border-black/20 hover:border-black/40 transition-colors bg-white"
              :aria-expanded="showDetails"
              :aria-label="t('pages.collection.explanation.toggleAriaLabel')"
            >
              <span class="text-lg font-medium">{{ t('pages.collection.explanation.toggleLabel') }}</span>
              <svg
                class="w-5 h-5 transition-transform"
                :class="{ 'rotate-180': showDetails }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div
              v-show="showDetails"
              class="border-x border-b border-black/20 bg-white p-6 space-y-6"
            >
              <p class="text-base md:text-lg text-black/80 leading-relaxed">
                {{ t('pages.collection.explanation.manifesto') }}
              </p>

              <p class="text-base md:text-lg text-black/80 leading-relaxed font-medium">
                {{ t('pages.collection.explanation.subtitle') }}
              </p>

              <ul class="space-y-4">
                <li class="flex gap-3">
                  <span class="flex-shrink-0 w-6 h-6 rounded-full bg-black/10 flex items-center justify-center text-sm font-medium mt-0.5">1</span>
                  <p class="text-base text-black/80 leading-relaxed">
                    <strong>{{ t('pages.collection.explanation.reason1Label') }}</strong> — {{ t('pages.collection.explanation.reason1') }}
                  </p>
                </li>
                <li class="flex gap-3">
                  <span class="flex-shrink-0 w-6 h-6 rounded-full bg-black/10 flex items-center justify-center text-sm font-medium mt-0.5">2</span>
                  <p class="text-base text-black/80 leading-relaxed">
                    <strong>{{ t('pages.collection.explanation.reason2Label') }}</strong> — {{ t('pages.collection.explanation.reason2') }}
                  </p>
                </li>
                <li class="flex gap-3">
                  <span class="flex-shrink-0 w-6 h-6 rounded-full bg-black/10 flex items-center justify-center text-sm font-medium mt-0.5">3</span>
                  <p class="text-base text-black/80 leading-relaxed">
                    <strong>{{ t('pages.collection.explanation.reason3Label') }}</strong> — {{ t('pages.collection.explanation.reason3') }}
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="px-0 md:px-4 lg:px-8 grid gap-8 lg:grid-cols-2 lg:gap-16 mx-auto max-w-screen-2xl">
        <div class="relative grid grid-cols-2 gap-y-4 sm:gap-x-6 sm:gap-y-10 lg:grid-cols-3 lg:gap-x-8 lg:col-span-2">
          <!-- Dynamic Products -->
          <CollectionProductCard 
            v-for="product in currentCollection.products" 
            :key="product.id"
            :product="product"
          />
        </div>
      </div>

      <div class="grid gap-8 lg:grid-cols-2 lg:gap-16 mx-auto max-w-screen-2xl px-4 py-8 sm:px-6 lg:px-8">
        <p class="text-center text-sm text-black mb-24 lg:col-span-2">
          <strong>{{ t('pages.collection.lastUpdate.label') }}</strong><br/>{{ lastUpdateText }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useI18n } from '#imports'
import { useCountdown, getNextSeasonLaunchCET } from '~/composables/useCountdown'
import CollectionProductCard from '~/components/CollectionProductCard.vue'
import { useSeasons } from '~/composables/useSeasons'
import { collectionsData, defaultCollection, type CollectionData } from '~/data/collections'

const props = defineProps({
  collectionId: {
    type: Number,
    default: 1
  }
})

const resolveCollection = (id: number): CollectionData => {
  if (id === 1) {
    return defaultCollection
  }
  const found = collectionsData.find((collection) => collection.id === id)
  return found ?? defaultCollection
}

const currentCollection = computed<CollectionData>(() => resolveCollection(props.collectionId))

const { t, locale } = useI18n()
const { getSeasonPeriod } = useSeasons()

const collectionPeriod = computed(() => {
  const id = currentCollection.value.id
  if (id >= 1 && id <= 4) {
    return getSeasonPeriod(id as 1 | 2 | 3 | 4, undefined, locale?.value ?? 'en')
  }
  const collectionKey = id.toString()
  return t(`pages.collection.collections.${collectionKey}.period`, {}, currentCollection.value.period)
})

const collectionDescription = computed(() => {
  const collectionKey = currentCollection.value.id.toString()
  return t(`pages.collection.collections.${collectionKey}.description`, {}, currentCollection.value.description)
})

const collectionHeading = computed(() => {
  const collectionKey = currentCollection.value.id.toString()
  return t(`pages.collection.collections.${collectionKey}.title`, {}, currentCollection.value.title || t('pages.collection.heading', { number: currentCollection.value.id }))
})

const lastUpdateText = computed(() => {
  const formatter = new Intl.DateTimeFormat(locale.value === 'it' ? 'it-IT' : 'en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
  return formatter.format(new Date())
})

const nextLaunch = ref<Date | null>(null)
onMounted(() => {
  nextLaunch.value = getNextSeasonLaunchCET()
})
const { formattedCountdown } = useCountdown(nextLaunch)

const showDetails = ref(false)
</script>
